import { INVENTAR_STATUS_OPTIONS } from "../constants/inventarStatus.js";

export const INVENTAR_HISTORY_FIELD_LABELS = {
  menge: "Bestand",
  projekttyp: "Bereich",
  kategorie: "Kategorie",
  bezeichnung: "Bezeichnung",
  hersteller: "Hersteller",
  seriennummer: "Seriennummer",
  status: "Status",
  standort_typ: "Standorttyp",
  standort_bez: "Standort",
  notiz: "Notiz",
  originalname: "Datei",
  beschreibung: "Beschreibung",
  typ_slug: "Dokumenttyp",
  dokument_geloescht: "Dokument entfernt",
};

export const INVENTAR_HISTORY_ACTION_META = {
  einbuchung: { label: "Erfasst", tone: "green", desc: "Artikel neu angelegt" },
  abbuchung: { label: "Entnommen", tone: "red", desc: "Aus dem Bestand entnommen" },
  änderung: { label: "Bearbeitet", tone: "amber", desc: "Stammdaten geändert" },
  umbuchung: { label: "Verschoben", tone: "blue", desc: "Standort oder Zuordnung geändert" },
  foto_hochgeladen: { label: "Foto", tone: "fuchsia", desc: "Foto hochgeladen" },
  dokument_hochgeladen: { label: "Dokument", tone: "indigo", desc: "Dokument hochgeladen" },
  menge_update: { label: "Bestand", tone: "cyan", desc: "Menge angepasst" },
};

const STATUS_LABEL = Object.fromEntries(INVENTAR_STATUS_OPTIONS.map((o) => [o.value, o.label]));

const TONE_TEXT_CLASS = {
  green: "text-green-300/85",
  red: "text-red-300/85",
  amber: "text-amber-300/85",
  blue: "text-blue-300/85",
  fuchsia: "text-fuchsia-300/85",
  indigo: "text-indigo-300/85",
  cyan: "text-cyan-300/85",
  neutral: "text-blue-200/75",
};

const ICON_TONE_CLASS = {
  green: "bg-white/[0.04] text-green-300/80",
  red: "bg-white/[0.04] text-red-300/80",
  amber: "bg-white/[0.04] text-amber-300/80",
  blue: "bg-white/[0.04] text-blue-300/80",
  fuchsia: "bg-white/[0.04] text-fuchsia-300/80",
  indigo: "bg-white/[0.04] text-indigo-300/80",
  cyan: "bg-white/[0.04] text-cyan-300/80",
  neutral: "bg-white/[0.04] text-blue-200/70",
};

export function inventarHistoryActionKey(aktion) {
  return String(aktion || "").toLowerCase();
}

export function inventarHistoryActionMeta(aktion) {
  const key = inventarHistoryActionKey(aktion);
  return INVENTAR_HISTORY_ACTION_META[key] || { label: aktion || "Eintrag", tone: "neutral", desc: "" };
}

export function inventarHistoryLabelClass(aktion) {
  const tone = inventarHistoryActionMeta(aktion).tone;
  return `text-[11px] font-medium ${TONE_TEXT_CLASS[tone] || TONE_TEXT_CLASS.neutral}`;
}

export function inventarHistoryIconClass(aktion) {
  const tone = inventarHistoryActionMeta(aktion).tone;
  return `flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${ICON_TONE_CLASS[tone] || ICON_TONE_CLASS.neutral}`;
}

export function tryParseHistoryDetails(v) {
  try {
    if (v == null || v === "") return null;
    if (typeof v === "string") return JSON.parse(v);
    if (typeof v === "object") return v;
    return null;
  } catch {
    return null;
  }
}

export function formatHistoryFieldValue(key, value) {
  if (value == null || value === "") return "—";
  if (key === "status") return STATUS_LABEL[String(value)] || String(value);
  if (key === "menge") {
    const n = Number(value);
    return Number.isFinite(n) ? `${n} St.` : String(value);
  }
  if (key === "notiz" && String(value).length > 80) return `${String(value).slice(0, 80)}…`;
  return String(value);
}

export function historyFieldEntries(parsed) {
  if (!parsed || typeof parsed !== "object") return [];
  return Object.entries(parsed).filter(([, v]) => v != null && v !== "");
}

export function buildHistoryDetailChips(parsed, { skipKeys = [] } = {}) {
  const skip = new Set(skipKeys);
  return historyFieldEntries(parsed)
    .filter(([k]) => !skip.has(k))
    .map(([key, value]) => ({
      key,
      label: INVENTAR_HISTORY_FIELD_LABELS[key] || key,
      value: formatHistoryFieldValue(key, value),
    }));
}

export function buildHistorySummary(aktion, parsed, mengeDelta = null) {
  const k = inventarHistoryActionKey(aktion);
  if (!parsed || typeof parsed !== "object") return inventarHistoryActionMeta(aktion).desc || "";

  if (k === "einbuchung") {
    const parts = [parsed.projekttyp, parsed.kategorie].filter(Boolean);
    return parts.length ? `Neu erfasst · ${parts.join(" · ")}` : "Artikel neu erfasst";
  }

  if (k === "menge_update" && parsed.menge != null) {
    if (mengeDelta && Number.isFinite(mengeDelta.from)) {
      return `Bestand ${formatHistoryFieldValue("menge", mengeDelta.from)} → ${formatHistoryFieldValue("menge", mengeDelta.to)}`;
    }
    return `Bestand auf ${formatHistoryFieldValue("menge", parsed.menge)} gesetzt`;
  }

  if (parsed.dokument_geloescht) {
    return `Dokument entfernt · ${parsed.dokument_geloescht}`;
  }

  if (k === "dokument_hochgeladen" || k === "foto_hochgeladen") {
    const name = parsed.originalname || parsed.beschreibung;
    if (name) return `${k === "foto_hochgeladen" ? "Foto" : "Dokument"} · ${name}`;
    return k === "foto_hochgeladen" ? "Foto hinzugefügt" : "Dokument hinzugefügt";
  }

  const entries = historyFieldEntries(parsed);
  if (entries.length === 1) {
    const [key, val] = entries[0];
    const label = INVENTAR_HISTORY_FIELD_LABELS[key] || key;
    return `${label} → ${formatHistoryFieldValue(key, val)}`;
  }
  if (entries.length > 1) {
    return `${entries.length} Felder aktualisiert`;
  }
  return inventarHistoryActionMeta(aktion).desc || "";
}

export function buildHistorySearchHaystack(entry, summary) {
  return [
    entry.aktion,
    inventarHistoryActionMeta(entry.aktion).label,
    entry.user_name,
    summary,
    typeof entry.details === "string" ? entry.details : JSON.stringify(entry.details || ""),
  ]
    .join(" ")
    .toLowerCase();
}

export function enrichInventarHistoryRows(rows) {
  const sortedDesc = [...rows]
    .map((e) => {
      const parsed = tryParseHistoryDetails(e.details);
      return { ...e, _parsed: parsed };
    })
    .sort((a, b) => new Date(b.zeit) - new Date(a.zeit));

  return sortedDesc.map((e, i) => {
    let mengeDelta = null;
    const k = inventarHistoryActionKey(e.aktion);
    if (k === "menge_update" && e._parsed?.menge != null) {
      const to = Number(e._parsed.menge);
      for (let j = i + 1; j < sortedDesc.length; j++) {
        const older = sortedDesc[j];
        const olderParsed = older._parsed || tryParseHistoryDetails(older.details);
        if (olderParsed?.menge != null && Number.isFinite(Number(olderParsed.menge))) {
          mengeDelta = { from: Number(olderParsed.menge), to };
          break;
        }
      }
    }
    const summary = buildHistorySummary(e.aktion, e._parsed, mengeDelta);
    const chips = buildHistoryDetailChips(e._parsed, {
      skipKeys: k === "menge_update" ? ["menge"] : [],
    });
    return {
      ...e,
      _mengeDelta: mengeDelta,
      _summary: summary,
      _chips: chips,
      _haystack: buildHistorySearchHaystack(e, summary),
    };
  });
}

export function historyDayKey(d) {
  return new Date(d).toISOString().slice(0, 10);
}

export function historyDayLabel(key) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const k = new Date(`${key}T00:00:00`);
  const diff = Math.round((k - today) / 86400000);
  if (diff === 0) return "Heute";
  if (diff === -1) return "Gestern";
  return k.toLocaleDateString("de-CH", {
    weekday: "long",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export function historyRelTime(d) {
  try {
    const rtf = new Intl.RelativeTimeFormat("de-CH", { numeric: "auto" });
    const now = Date.now();
    const ts = new Date(d).getTime();
    const mins = Math.round((ts - now) / 60000);
    if (Math.abs(mins) < 1) return "gerade eben";
    if (Math.abs(mins) < 60) return rtf.format(mins, "minute");
    const h = Math.round(mins / 60);
    if (Math.abs(h) < 24) return rtf.format(h, "hour");
    const dys = Math.round(h / 24);
    if (Math.abs(dys) < 7) return rtf.format(dys, "day");
    return rtf.format(Math.round(dys / 7), "week");
  } catch {
    return "";
  }
}

export function formatHistoryTime(d) {
  return new Date(d).toLocaleString("de-CH", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
